import { BorderRadiusCorners, Box, Text, Touchable } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import { isValidPeriodOrigin } from 'modules/shift_management/components/ShiftManagement/CopyPeriodWizard/utils'
import UnsafeRadioInput from 'modules/shift_management/components/ShiftManagement/UnsafeRadioInput'
import type CopyPeriodForm from 'modules/shift_management/lib/forms/CopyPeriodForm'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'

import { DateValues } from 'modules/shift_management/components/ShiftManagement/CopyPeriodWizard/utils'

interface Props {
  onChangePeriod: (values: DateValues) => void
  onCopyPeriod: () => void
  isSelected: boolean
  copyPeriodForm: CopyPeriodForm
  title: string
  description: string
  days: number
}

const CopySchedule = ({
  onChangePeriod,
  onCopyPeriod,
  isSelected,
  copyPeriodForm,
  title,
  description,
  days,
}: Props) => {
  const startTimeField = copyPeriodForm.get('start_at')
  const endTimeField = copyPeriodForm.get('end_at')

  const borderRadius: BorderRadiusCorners = {
    all: 'abs008',
  }

  const isValidPeriod = isValidPeriodOrigin(days)

  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()

  const daysToBeCopiedMessage = i18n.t(
    'shift_management.copy_schedule_period.origin_step.schedule.message',
    {
      days,
    }
  )

  return (
    <Box
      border={{
        all: {
          width: 's2',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={borderRadius}
      background="grey000White"
      width="s600"
    >
      <Box
        alignItems="center"
        borderRadius={borderRadius}
        padding="s12"
        flexDirection="row"
        justifyContent="spaceBetween"
        background="grey000White"
      >
        <Box flexDirection="column" justifyContent="spaceBetween" alignItems="flexStart" gap="s4">
          <Text size="300" weight="semibold" color="grey800">
            {title}
          </Text>

          <Text.Normal size="300" weight="normal" color="grey800">
            {description}
          </Text.Normal>
        </Box>

        <Touchable onClick={onCopyPeriod}>
          <UnsafeRadioInput selected={isSelected} />
        </Touchable>
      </Box>

      {isSelected && (
        <Box
          alignItems="center"
          borderRadius={borderRadius}
          padding="s12"
          flexDirection="row"
          justifyContent="spaceBetween"
          background="grey000White"
        >
          <Box flexDirection="column" justifyContent="spaceBetween" gap="s12">
            <DayPicker
              mode="range"
              onChange={onChangePeriod}
              value={[startTimeField.value, endTimeField.value]}
              focusTrapActive={false}
              mandatory
              firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
              minDate={minDate}
              maxDate={maxDate}
            />

            {!isValidPeriod && (
              <Text size="200" color="radicalRed1000" capitalize>
                {i18n.t('shift_management.copy_schedule_period.origin_step.error_message')}
              </Text>
            )}

            {!!days && (
              <Box background="grey300" borderRadius={borderRadius} padding="s24">
                <Text.Normal color="grey1000">{daysToBeCopiedMessage}</Text.Normal>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default observer(CopySchedule)
